import * as Sentry from "@sentry/react";
import type { IApiClient } from "@/lib/__shared__/apiClient";
import type { ApiResponse } from "@coffitivity/shared";

interface FavoriteTrackMutationResponse {
	created?: boolean;
	deleted?: boolean;
}

export interface IFavoriteTrackRepository {
	get: () => Promise<string[]>;
	add: (trackId: string) => Promise<FavoriteTrackMutationResponse>;
	remove: (trackId: string) => Promise<FavoriteTrackMutationResponse>;
}

export class FavoriteTrackRepository implements IFavoriteTrackRepository {
	private apiClient: IApiClient;

	constructor(apiClient: IApiClient) {
		this.apiClient = apiClient;
	}

	async get(): Promise<string[]> {
		try {
			const response = await this.apiClient.fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/user/favorite-tracks`,
				{ method: "GET" },
			);
			const json: ApiResponse<string[]> = await response.json();

			if (response.ok && json.success) {
				return json.data;
			}

			if (!json.success) {
				throw new Error(json.message);
			}

			throw new Error("Failed to fetch Favorite Tracks.");
		} catch (error) {
			Sentry.captureException(error);
			return [];
		}
	}

	async add(trackId: string): Promise<FavoriteTrackMutationResponse> {
		return this.mutate(trackId, "PUT");
	}

	async remove(trackId: string): Promise<FavoriteTrackMutationResponse> {
		return this.mutate(trackId, "DELETE");
	}

	private async mutate(
		trackId: string,
		method: "PUT" | "DELETE",
	): Promise<FavoriteTrackMutationResponse> {
		try {
			const response = await this.apiClient.fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/user/favorite-tracks/${trackId}`,
				{ method },
			);
			const json: ApiResponse<FavoriteTrackMutationResponse> =
				await response.json();

			if (response.ok && json.success) {
				return json.data;
			}

			if (!json.success) {
				throw new Error(json.message);
			}

			throw new Error("Failed to update Favorite Track.");
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(error);
		}
	}
}
